import {
  ImpactToolInput,
  ImpactToolLiveResult,
  ImpactToolResult,
} from '~/types/impactTool';
import {
  mileageParameters,
  travelTimeParameters,
  resultSkeleton,
  liveResultSkeleton,
} from '.';
import { forecast } from './helpers';
import { healtcare_parameters } from './parameters';

/** Sum of all values should be 100% */
export const sumPercentageUsageVehicles = (
  car: string,
  bike: string,
  parkandride: string,
  trainandwalk: string
): number => {
  return (
    parseInt(car) +
    parseInt(bike) +
    parseInt(parkandride) +
    parseInt(trainandwalk)
  );
};

/** Calculate amount of new and used parking */
export const amountNewParking = (
  number_new_parking: string | number,
  usage_new_parking: string | number
): number => {
  let sum = 0;
  if (typeof number_new_parking === 'string') {
    number_new_parking = parseInt(number_new_parking);
  }
  if (typeof usage_new_parking === 'string') {
    usage_new_parking = parseInt(usage_new_parking);
  }
  sum = (number_new_parking * usage_new_parking) / 100;
  if (typeof sum === 'number' && sum > 0) {
    return Math.round(sum);
  }
  return 0;
};

/** 90% Percent of new parking spots are used usually */
export const busyParkingSpots = (
  number_new_parking: string | number
): number => {
  if (
    typeof number_new_parking === 'string' &&
    parseInt(number_new_parking) > 0
  ) {
    return parseInt(number_new_parking) * 0.9;
  } else if (typeof number_new_parking === 'number') {
    return number_new_parking * 0.9;
  }
  return 0;
};

/** Ratio of used new parking by transport */
export const newUsersPerTransport = (
  newUsesOfParking: number,
  percentage_car: number,
  percentage_parkandride: number,
  percentage_trainandwalk: number,
  percentage_bike: number
): {
  number_car: number;
  number_parkandride: number;
  number_trainandwalk: number;
  number_bike: number;
} => {
  return {
    number_car: percentage_car * newUsesOfParking,
    number_parkandride: percentage_parkandride * newUsesOfParking,
    number_trainandwalk: percentage_trainandwalk * newUsesOfParking,
    number_bike: percentage_bike * newUsesOfParking,
  };
};

/** Calculate CO₂ emissions which get reduced and their implicit costs */
export const reduced_co2_emission = (
  reduced_distance_pedelec: number,
  reduced_distance_car: number
): Pick<
  ImpactToolResult,
  | 'reduced_co2_emissions'
  | 'reduced_co2_municipality'
  | 'reduced_co2_costs'
  | 'reduced_co2_costs_municipality'
> => {
  // Emissions
  const pedelec =
    mileageParameters.co2_emission_pedelec_per_km * reduced_distance_pedelec;
  const car = -reduced_distance_car * mileageParameters.co2_emission_car_per_km;
  const sum_co2 = pedelec + car;

  // Emission Costs
  const pedelec_costs =
    (mileageParameters.co2_per_tons * pedelec) / 1000 / 1000;
  const car_costs = (mileageParameters.co2_per_tons * car) / 1000 / 1000;
  const sum_costs = pedelec_costs + car_costs;

  return {
    reduced_co2_emissions: sum_co2 / 1000 / 1000,
    reduced_co2_municipality:
      (car * mileageParameters.municipipality_rate + pedelec) / 1000 / 1000,
    reduced_co2_costs: sum_costs,
    reduced_co2_costs_municipality:
      car_costs * mileageParameters.municipipality_rate + pedelec_costs,
  };
};

/** Costs through air pollution for society */
export const air_polutant_costs = (
  reduced_distance_pedelec: number,
  reduced_distance_car: number
): Pick<ImpactToolResult, 'reduced_air_pollution'> => {
  const pedelec =
    (mileageParameters.air_pollutant_costs_pedelec * reduced_distance_pedelec) /
    100;
  const car =
    (mileageParameters.air_pollutant_costs_car * reduced_distance_car) / 100;

  return { reduced_air_pollution: -car + pedelec };
};

/** Sum of all operational costs of vehicles: bike, pedelec and car */
export const operational_costs = (
  reduced_distance_bike: number,
  reduced_distance_pedelec: number,
  reduced_distance_car: number
): Pick<ImpactToolResult, 'reduced_vehicle_costs'> => {
  const pedelec =
    mileageParameters.pedelec_costs_per_km * reduced_distance_pedelec;
  const bike = -mileageParameters.bike_costs_per_km * reduced_distance_bike;
  const car = -mileageParameters.car_costs_per_km * reduced_distance_car;

  return { reduced_vehicle_costs: car + pedelec + bike };
};

/** Sum of accident costs of all transports for intakt traffic */
export const accident_costs = (
  reduced_distance_bike: number,
  reduced_distance_pedelec: number,
  reduced_distance_car: number
): Pick<ImpactToolResult, 'reduced_accident_costs'> => {
  const pedelec =
    mileageParameters.accident_costs_pedelec_per_km * reduced_distance_pedelec;
  const bike =
    -mileageParameters.accident_costs_bike_per_km * reduced_distance_bike;
  const car =
    -mileageParameters.accident_costs_car_per_km * reduced_distance_car;

  return { reduced_accident_costs: car + pedelec + bike };
};

/** "Reisezeitänderung" in sum and costs for society.
 * These calculations have been thought as Matrix calculations like numpy, without extra tool. */
export const travel_time_change = (
  new_usages: number,
  car_rate: number,
  parkandride_rate: number,
  transport_rate: number,
  bike_rate: number,
  number_existing_user: number,
  change_duration_distance: number
): Pick<ImpactToolResult, 'reduced_traveltime' | 'reduced_society_costs'> => {
  // These matrices have all this shape: [car, parkandride, public transport, bike, existing users]
  const trips_count = [
    new_usages * car_rate,
    new_usages * parkandride_rate,
    new_usages * transport_rate,
    new_usages * bike_rate,
    number_existing_user,
  ];
  const ptc = travelTimeParameters.travel_duration_average_minutes;
  // Average intake time bike
  const aitb = (3.5 / 18) * 60;
  const travel_change_one_way = [
    trips_count[0] *
      (ptc +
        travelTimeParameters.terminal_latency +
        aitb -
        (ptc + travelTimeParameters.terminal_latency + aitb) *
          travelTimeParameters.travel_duration_change_car),
    trips_count[1] *
      (aitb +
        travelTimeParameters.terminal_latency -
        (aitb + travelTimeParameters.terminal_latency) *
          travelTimeParameters.travel_duration_change_parkandride),

    (travelTimeParameters.terminal_latency +
      aitb -
      (travelTimeParameters.terminal_latency + aitb) *
        travelTimeParameters.travel_duration_change_publictransport) *
      trips_count[2],
    trips_count[3] *
      (ptc +
        travelTimeParameters.terminal_latency +
        aitb -
        (ptc + travelTimeParameters.terminal_latency + aitb) *
          travelTimeParameters.travel_duration_change_bike),
    trips_count[4] * change_duration_distance,
  ];
  const travel_change_round_trip = travel_change_one_way.map((x) => x * 2);
  const minutes_per_year = [
    (travelTimeParameters.pupils_rate *
      travelTimeParameters.travel_days_pupils +
      travelTimeParameters.travel_days_adults *
        travelTimeParameters.adults_rate) *
      travel_change_round_trip[0],
    (travelTimeParameters.pupils_rate *
      travelTimeParameters.travel_days_pupils +
      travelTimeParameters.travel_days_adults *
        travelTimeParameters.adults_rate) *
      travel_change_round_trip[1],
    (travelTimeParameters.pupils_rate *
      travelTimeParameters.travel_days_pupils +
      travelTimeParameters.travel_days_adults *
        travelTimeParameters.adults_rate) *
      travel_change_round_trip[2],
    (travelTimeParameters.pupils_rate *
      travelTimeParameters.travel_days_pupils +
      travelTimeParameters.travel_days_adults *
        travelTimeParameters.adults_rate) *
      travel_change_round_trip[3],
    (travelTimeParameters.pupils_rate *
      travelTimeParameters.travel_days_pupils +
      travelTimeParameters.travel_days_adults *
        travelTimeParameters.adults_rate) *
      travel_change_round_trip[4],
  ];
  const hours_per_year = minutes_per_year.map((x) => x / 60);

  const sum_reduced_travel_time = hours_per_year.reduce((pv, cv) => {
    if (!isNaN(pv) && !isNaN(cv)) {
      return pv + cv;
    } else {
      return pv;
    }
  });

  // Sum up all values
  return {
    reduced_traveltime: sum_reduced_travel_time,
    reduced_society_costs:
      sum_reduced_travel_time *
      travelTimeParameters.cost_rate_usage_euro_per_hour,
  };
};

export const healthcare_costs = (
  active_persons: number,
  distance_active_persons: number
): Pick<ImpactToolResult, 'reduced_healthcare_costs'> => {
  const for_active_persons =
    -active_persons * healtcare_parameters.health_effect_per_person_cost;

  const mortality =
    -distance_active_persons *
    healtcare_parameters.reduced_mortality_cost_per_km;

  return {
    reduced_healthcare_costs: mortality + for_active_persons,
  };
};

export const activePersons = (
  distance_bikeandride: string | number,
  new_usages: number
): Pick<ImpactToolResult, 'active_persons'> => {
  if (typeof distance_bikeandride === 'string') {
    distance_bikeandride = parseFloat(distance_bikeandride);
  }

  const result =
    forecast(
      distance_bikeandride,
      [0.03, 0.06, 0.09, 0.13, 0.16, 0.2, 0.22, 0.25, 0.28, 0.31, 0.34],
      [1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5, 6.0]
    ) * new_usages;

  return {
    active_persons: result,
  };
};

/** Sum distance which "active persons" will travel then */
export const predictionDistanceActivePersons = (
  distance_bikeandride: string | number,
  active_persons: number
): Pick<ImpactToolResult, 'distance_active_persons'> => {
  if (typeof distance_bikeandride === 'string') {
    distance_bikeandride = parseFloat(distance_bikeandride);
  }

  if (active_persons) {
    const result =
      active_persons *
      forecast(
        distance_bikeandride,
        [4.75, 5.0, 5.25, 5.5, 5.75, 6.0, 6.25, 6.5, 6.75, 7.0, 7.25],
        [1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5, 6.0]
      ) *
      2 *
      travelTimeParameters.travel_days_adults;

    return {
      distance_active_persons: result,
    };
  }
  return {
    distance_active_persons: 0,
  };
};

/** "Fahrleiständerungen": Change in distance of ways per transport */
export const mileage_change = (
  new_usages: number,
  percentage_car: number,
  percentage_parkandride: number,
  percentage_trainandwalk: number,
  percentage_bike: number,
  percentage_pedelec: number,
  distance_car_before_shift: number,
  distance_car_after_shift: number,
  distance_bike_before_shift: number
): Pick<
  ImpactToolResult,
  'reduced_car_distance' | 'change_bike_distance' | 'change_bike_distance_all'
> => {
  // These matrices have all this shape: [car, parkandride, public transport, bike]
  const trips_count = [
    new_usages * percentage_car,
    new_usages * percentage_parkandride,
    new_usages * percentage_trainandwalk,
    new_usages * percentage_bike,
  ];

  const distance_bike_one_way = [
    trips_count[0] * distance_car_after_shift,
    trips_count[1] * distance_car_after_shift,
    trips_count[2] * distance_car_after_shift,
    trips_count[3] * (distance_car_after_shift - distance_bike_before_shift),
  ];
  const distance_bike_round_trip = distance_bike_one_way.map((x) => x * 2);

  const distance_car_one_way = [
    (trips_count[0] * distance_car_before_shift) /
      travelTimeParameters.occupany_rate_people_car,
    (trips_count[1] * distance_car_after_shift) /
      travelTimeParameters.occupany_rate_people_car,
  ];
  const distance_car_round_trip = distance_car_one_way.map((x) => x * 2);

  const extrapolation_year_bike = distance_bike_round_trip.map(
    (x) => x * travelTimeParameters.travel_days_adults
  );
  const extrapolation_year_car = distance_car_round_trip.map(
    (x) => x * travelTimeParameters.travel_days_adults
  );

  const sum_bike = extrapolation_year_bike.reduce((pv, cv) => {
    if (!isNaN(pv) && !isNaN(cv)) {
      return pv + cv;
    } else {
      return pv;
    }
  });

  const sum_car = extrapolation_year_car.reduce((pv, cv) => {
    if (!isNaN(pv) && !isNaN(cv)) {
      return pv + cv;
    } else {
      return pv;
    }
  });

  return {
    reduced_car_distance: sum_car,
    change_bike_distance_all: sum_bike,
    change_bike_distance:
      pedelec_change_distance(new_usages, percentage_pedelec, sum_bike)
        .change_pedelec_distance - sum_bike,
  };
};

export const pedelec_change_distance = (
  new_usages: number,
  percentage_pedelec: number,
  change_bike_distance_all: number
): Pick<ImpactToolResult, 'change_pedelec_distance'> => {
  // Difference between bike_all and bike: bike_all contains also pedelec
  const number_ways_bike_all =
    2 * new_usages * travelTimeParameters.travel_days_adults;
  const number_ways = number_ways_bike_all * percentage_pedelec;
  const number_ways_bike = number_ways_bike_all - number_ways;

  const distance_bike =
    change_bike_distance_all /
    (number_ways_bike + mileageParameters.pedelec_bike_ratio * number_ways);
  const distance_pedelec = distance_bike * mileageParameters.pedelec_bike_ratio;

  return {
    change_pedelec_distance: distance_pedelec * number_ways,
  };
};

/** Calcualte all results for ergebnis page */
export const calculate_result = (values: ImpactToolInput): ImpactToolResult => {
  let result = resultSkeleton();

  result.location_name = values.location_name;
  result.project_description = values.project_description;

  result.number_new_parking = parseInt(values.number_new_parking);
  result.usage_new_parking = parseInt(values.usage_new_parking) / 100;
  result.sum_construction_cost = parseInt(values.sum_construction_cost);
  result.own_calculation = values.own_calculation;

  const number_new_parking_spots_used =
    busyParkingSpots(result.number_new_parking) * result.usage_new_parking;

  const active_persons_result = activePersons(
    values.distance_bikeandride,
    number_new_parking_spots_used
  );

  const distance_active_persons = predictionDistanceActivePersons(
    values.distance_bikeandride,
    active_persons_result.active_persons
  );

  const mileage_change_result = mileage_change(
    number_new_parking_spots_used,
    parseInt(values.percentage_car) / 100,
    parseInt(values.percentage_parkandride) / 100,
    parseInt(values.percentage_trainandwalk) / 100,
    parseInt(values.percentage_bike) / 100,
    parseInt(values.percentage_pedelec) / 100,
    parseFloat(values.distance_car),
    parseFloat(values.distance_bikeandride),
    parseFloat(values.distance_bike)
  );

  const pedelec_change_distance_result = pedelec_change_distance(
    number_new_parking_spots_used,
    parseInt(values.percentage_pedelec) / 100,
    mileage_change_result.change_bike_distance_all
  );

  result = {
    ...result,
    ...reduced_co2_emission(
      pedelec_change_distance_result.change_pedelec_distance,
      mileage_change_result.reduced_car_distance
    ),
    ...air_polutant_costs(
      pedelec_change_distance_result.change_pedelec_distance,
      mileage_change_result.reduced_car_distance
    ),
    ...operational_costs(
      mileage_change_result.change_bike_distance,
      pedelec_change_distance_result.change_pedelec_distance,
      mileage_change_result.reduced_car_distance
    ),
    ...accident_costs(
      mileage_change_result.change_bike_distance,
      pedelec_change_distance_result.change_pedelec_distance,
      mileage_change_result.reduced_car_distance
    ),
    ...travel_time_change(
      number_new_parking_spots_used,
      parseInt(values.percentage_car) / 100,
      parseInt(values.percentage_parkandride) / 100,
      parseInt(values.percentage_trainandwalk) / 100,
      parseInt(values.percentage_bike) / 100,
      result.number_new_parking * 0.9 - number_new_parking_spots_used,
      parseInt(values.change_duration_distance)
    ),
    ...healthcare_costs(
      active_persons_result.active_persons,
      distance_active_persons.distance_active_persons
    ),
    ...active_persons_result,
    ...distance_active_persons,
    ...mileage_change_result,
    ...pedelec_change_distance_result,
  };

  return result;
};

/** Calculate only results for live result */
export const calculate_live_result = (
  values: ImpactToolInput
): ImpactToolLiveResult => {
  let result = liveResultSkeleton();

  const number_new_parking_spots_used =
    (busyParkingSpots(parseInt(values.number_new_parking)) *
      parseInt(values.usage_new_parking)) /
    100;

  const mileage_change_result = mileage_change(
    number_new_parking_spots_used,
    parseInt(values.percentage_car) / 100,
    parseInt(values.percentage_parkandride) / 100,
    parseInt(values.percentage_trainandwalk) / 100,
    parseInt(values.percentage_bike) / 100,
    parseInt(values.percentage_pedelec) / 100,
    parseFloat(values.distance_car),
    parseFloat(values.distance_bikeandride),
    parseFloat(values.distance_bike)
  );

  const pedelec_change_distance_result = pedelec_change_distance(
    number_new_parking_spots_used,
    parseInt(values.percentage_pedelec) / 100,
    mileage_change_result.change_bike_distance_all
  );

  result = {
    ...result,
    reduced_co2_emissions: reduced_co2_emission(
      pedelec_change_distance_result.change_pedelec_distance,
      mileage_change_result.reduced_car_distance
    ).reduced_co2_emissions,
    reduced_car_distance: mileage_change_result.reduced_car_distance,
  };

  return result;
};
